// ============================================================
//  ★ 데이터 SEAM — 개발자 작업은 "여기"만 ★
// ------------------------------------------------------------
//  핸드오프(.handoff/gr-gr01__20260826-1424)가 지정한 유일한 데이터 접점.
//  fn 내부를 2.0 API 호출로 교체했다. 컬럼/필터/테이블 UI 는 훅 계약(contents/totalCount)만 소비한다.
//  BE: POST /api/v1/get-normal-inbounds (NormalInboundSearchController, 1.0 MENU_CD='GR01_HEAD' 대체)
//  - 필터: shipperCode(eq) · ordOptionNumbers(in) · orderType(eq) · transactionType(eq)
//  - 물류운영팀 계정은 BE 가 updBy=본인 이메일로 한 번 더 좁힌다(NormalInboundSearchService.resolveUpdBy)
// ============================================================
import { throwIfCutShort, useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import request from '@/utils/request'
import { GR01_FETCH_SIZE } from './consts'

/** 화면 행 — dataIndex 는 1.0 컬럼명을 유지한다(핸드오프 spec.md 컬럼 계약). */
export interface GeneralInboundRow {
  /** 확정(confirm-inbound ids) · 저장(request-inbound items[].id) 대상 식별자. 화면 비표시 */
  _id: number
  /** 화주명 — SPR_NM(화주코드) 의 표시용 짝. 저장 payload 미포함 */
  _shipperName: string
  /** 신규 행(엑셀 업로드) 표시 — id 없이 생성 요청으로 나간다 */
  _isNew?: boolean
  SPR_NM: string
  PLN_DATE: string
  ORD_OPT_NO: string
  /** 입고유형 — 화면 컬럼 아님. 저장 시 필수값이라 행에 보존한다 */
  ORD_TYPE: string
  /** 거래유형 — 원장 lot.lotAttr5 원본값('MSSB2B' 또는 빈값=온라인) */
  LOT_ATTR5: string
  BRD_NM: string
  GDS_GROUP1: string
  GDS_NM: string
  STL_NO: string
  /**
   * 빈 값은 `null` 이 아니라 `''` 다. pbo-core amountInput 렌더러가 `value == null` 이면
   * 입력창 대신 글자 `-` 를 돌려줘(dist/index.mjs:6831) 그 셀을 클릭해도 편집이 안 된다.
   * 셀 편집이 문자열을 넣기도 한다(`onChange` 가 `target.value` 를 그대로 준다) — 실제 값 범위를 적는다.
   */
  ORD_QTY: number | string | null
  WH_NM: string
  AP_PRICE: number | null
  WH_AREA: string
  BARCODE: string
  SKU_ID: string
  UID: string
  /** 1.0 GR01 그리드 메타와 엑셀 계약의 원본 키가 소문자 `option_code`다. */
  option_code: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
  TRS_INVOICE_NO: string
  COM_NM: string
}

export interface GeneralInboundParams {
  /** 화주코드 (필수 필터) */
  shipperCode?: string
  /** 입고번호 다건 — BE 상한 200개 */
  ordOptionNumbers?: string[]
  orderType?: string
  transactionType?: string
}

interface NormalInboundItem {
  id: number
  shipperCode: string
  shipperName?: string | null
  plnDate?: string | number[] | null
  ordOptionNumber?: string | null
  orderType?: string | null
  transactionType?: string | null
  brandName?: string | null
  goodsGroup?: string | null
  goodsName?: string | null
  styleNumber?: string | null
  quantity: number
  purchasePrice?: number | null
  warehouseName?: string | null
  warehouseArea?: string | null
  barcode?: string | null
  skuId?: string | null
  uid?: string | null
  optionCode?: string | null
  boxNumber?: string | null
  companyName?: string | null
  createdAt?: string | number[] | null
  createdBy?: string | null
  updatedAt?: string | number[] | null
  updatedBy?: string | null
}

interface PageInfoItems {
  items: NormalInboundItem[]
  pageInfo: { page: number; size: number; totalCount: number; totalPages: number }
}


const pad2 = (n: number) => String(n).padStart(2, '0')

/** BE LocalDate/LocalDateTime 은 문자열 또는 [y,M,d,...] 배열로 온다 — 둘 다 받는다 */
const fmt = (value: unknown, length: number): string => {
  if (Array.isArray(value)) {
    const [y, mo = 1, d = 1, h = 0, mi = 0, s = 0] = value as number[]
    return `${y}-${pad2(mo)}-${pad2(d)} ${pad2(h)}:${pad2(mi)}:${pad2(s)}`.slice(0, length)
  }
  return typeof value === 'string' && value ? value.slice(0, 19).replace('T', ' ').slice(0, length) : ''
}

const toRow = (item: NormalInboundItem): GeneralInboundRow => ({
  _id: item.id,
  _shipperName: item.shipperName ?? '',
  SPR_NM: item.shipperCode,
  PLN_DATE: fmt(item.plnDate, 10),
  ORD_OPT_NO: item.ordOptionNumber ?? '',
  ORD_TYPE: item.orderType ?? '',
  LOT_ATTR5: item.transactionType ?? '',
  BRD_NM: item.brandName ?? '',
  GDS_GROUP1: item.goodsGroup ?? '',
  GDS_NM: item.goodsName ?? '',
  STL_NO: item.styleNumber ?? '',
  ORD_QTY: item.quantity,
  WH_NM: item.warehouseName ?? '',
  AP_PRICE: item.purchasePrice ?? null,
  WH_AREA: item.warehouseArea ?? '',
  BARCODE: item.barcode ?? '',
  SKU_ID: item.skuId ?? '',
  UID: item.uid ?? '',
  option_code: item.optionCode ?? '',
  INS_DATE: fmt(item.createdAt, 19),
  INS_BY: item.createdBy ?? '',
  UPD_DATE: fmt(item.updatedAt, 19),
  UPD_BY: item.updatedBy ?? '',
  TRS_INVOICE_NO: item.boxNumber ?? '',
  COM_NM: item.companyName ?? '',
})

/** 조회 파라미터 → BE 요청 본문. 빈 값은 실어 보내지 않는다(BE eq 조건이라 빈 문자열이면 0건) */
export const toSearchBody = (params: GeneralInboundParams): Record<string, unknown> => ({
  ...(params.shipperCode ? { shipperCode: params.shipperCode } : {}),
  ...(params.ordOptionNumbers?.length ? { ordOptionNumbers: params.ordOptionNumbers } : {}),
  ...(params.orderType ? { orderType: params.orderType } : {}),
  ...(params.transactionType ? { transactionType: params.transactionType } : {}),
})

/** 구간 경계에서 겹친 행을 거르는 기본키(ArrivalNotice PK) — 모듈 상수라 참조가 바뀌지 않는다. */
const rowIdOf = (row: GeneralInboundRow): number => row._id

/**
 * 일반입고 목록 — 전량 조회(LOGISPRD-15268). BE 상한(1,000)씩 끊어 받고 화면은 10,000건씩 나눈다.
 *
 * 화주코드가 없으면 조회하지 않는다.
 *
 * 화주명은 화면의 필수 필터인데(spec.md) BE 는 shipperCode 를 optional 로 받는다.
 * 빈 조건으로 나가면 GR01 메뉴의 전건이 상한(10만 행)까지 딸려온다. 호출부가 enabled 를
 * 빠뜨렸을 때 그 사고가 나지 않도록 규칙을 데이터 레이어에 둔다 — 호출 규약이 아니라 구조로 막는다.
 *
 * 탭 복귀·재연결에 다시 받지 않는다(공용 훅 staleTime: Infinity). [조회] 버튼이 부르는 명시적 refetch 만 다시 받는다.
 * 실패 알럿은 공용 훅 meta(errorToast)로 전역이 띄운다.
 */
export const useGetInboundList = (params: GeneralInboundParams, enabled = true): UseFetchAllRowsResult<GeneralInboundRow> =>
  useFetchAllRows({
    queryKey: generalInboundQueryKeys.list(params),
    fetchPage: async (page, size = GR01_FETCH_SIZE) => {
      const res = await request.post<ApiResponseV2<PageInfoItems>, Record<string, unknown>>('/api/v1/get-normal-inbounds', {
        ...toSearchBody(params),
        page,
        size,
      })
      // pageInfo 가 빠진 응답을 받은 건수로 메우지 않는다 — 첫 구간에서 조용히 멈춘다. 없으면 여기서 던져 실패 알럿으로 드러낸다(rgr13 선례).
      const { items, pageInfo } = res.data
      throwIfCutShort(page, size, items.length, pageInfo.totalCount)
      return { items: items.map(toRow), totalCount: pageInfo.totalCount }
    },
    chunkSize: GR01_FETCH_SIZE,
    getKey: rowIdOf,
    enabled: enabled && Boolean(params.shipperCode),
  })

/**
 * 조회 키.
 *
 * 맨 앞이 라우트 메뉴코드(gr07)인 것은 규약이다 — 탭을 닫을 때 MenuOnSessionWrap.remove 가
 * 메뉴코드가 든 캐시만 지운다(`utils/storage/MenuOnSessionWrap.ts:110-115`). 대소문자가 그대로여야
 * 걸린다: 탭 엔트리의 code 는 MENU_CD 를 소문자로 바꾼 값이다(`services/auth/useAuthQueries.ts:54`).
 *
 * 마운트마다 초기화되는 값(조회 카운터 등)을 키에 넣지 않는다. 넣으면 탭을 다녀올 때마다 키가
 * 어긋나 캐시가 무용지물이 되고 전량 재조회가 된다. 같은 조건 재조회는 키가 아니라 refetch 로 낸다.
 */
export const generalInboundQueryKeys = {
  all: ['gr07', 'GENERAL_INBOUND'] as const,
  list: (params: GeneralInboundParams) => [...generalInboundQueryKeys.all, 'LIST', params] as const,
}
